import { useCallback, useState } from 'react';
import { isSmallerSelector } from '../../../layout/mediaquery';
import { useSelector } from 'react-redux';
import { Tooltip } from '@comeon/ui';
import styled from 'styled-components';
import breakpoint from '../../../../utils/style-utils';
import { SPORTSBOOK_PORTAL_ID } from '../sportsbook-constants';

const StyledTooltipContent = styled(Tooltip.Content)`
	padding: 12px;
	border-radius: var(--sportsbook-rounded-md);
	border-bottom-right-radius: 2px;
	background-color: var(--sportsbook-tooltip-bg, var(--sportsbook-bg-tertiary));
	outline: var(--sportsbook-border-primary);
	box-shadow: var(--sportsbook-shadow-secondary);
	z-index: 0;

	${breakpoint.lg`
		border-radius: var(--sportsbook-rounded-md);
	`};
`;

const StyledPopoverTrigger = styled(Tooltip.Trigger)`
	outline: var(--sportsbook-border-primary);
`;

const StyledTooltipArrow = styled(Tooltip.Arrow)`
	fill: var(--sportsbook-tooltip-bg, var(--sportsbook-bg-tertiary));

	& path:first-of-type {
		stroke: var(--sportsbook-tooltip-bg, var(--sportsbook-bg-tertiary));
	}

	& path:last-of-type {
		stroke: var(--sportsbook-tooltip-bg, var(--sportsbook-bg-tertiary));
	}
`;

type PopoverIconProps = {
	children: React.ReactNode;
	className?: string;
	label: string;
};

/**
 * @title Popover Icon
 * @description A popover icon component that can be used to display a popover on click or hover.
 * @prop {React.ReactNode} children - The content to display inside the popover.
 * @prop {string} className - Override styling of the content inside the popover.
 */
const SportsbookTooltip: React.FC<PopoverIconProps> = ({ children, label, className }) => {
	const [isOpen, setIsOpen] = useState(false);
	const isSmaller = useSelector(isSmallerSelector);

	const handleClick = () => {
		if (isSmaller) {
			setIsOpen(open => !open);
		}
	};

	const handleMouseEnter = useCallback(() => {
		if (!isSmaller) {
			setIsOpen(true);
		}
	}, [isSmaller]);

	const handleMouseLeave = useCallback(() => {
		if (!isSmaller) {
			setIsOpen(false);
		}
	}, [isSmaller]);

	return (
		<Tooltip.Root placement="left" onOpenChange={setIsOpen} isOpen={isOpen}>
			<StyledPopoverTrigger onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
				{children}
			</StyledPopoverTrigger>
			<StyledTooltipContent className={className} portalId={SPORTSBOOK_PORTAL_ID}>
				<StyledTooltipArrow tipRadius={1} />
				{label}
			</StyledTooltipContent>
		</Tooltip.Root>
	);
};

export default SportsbookTooltip;
